// src/ui/screens/post/widgets/UserRecommendationItem.jsx
//
// Column-layout counterpart to UserItem (which is row-based). Carries the
// same elements — avatar, name, verification badge, username, ID
// verification badge, "Listed" badge, subscribe button — stacked
// vertically so it fits inside a horizontal "Recommended for you" rail
// (or a grid on the Atoms tab).

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db, auth } from '../../../../firebase';
import { useSubscribe } from '../../../../utils/subscribeProvider';
import UserAvatar from '../../../../components/UserAvatar';
import VerificationBadge from '../../../../components/VerificationBadge';
import IDVerificationBadge from '../../../../components/IdVerificationBadge';

const CARD = '#23262C';
const ORANGE = '#FF8C08';

export default function UserRecommendationItem({ user, width = 104 }) {
  const navigate = useNavigate();
  const subscribe = useSubscribe();
  const [isSeller, setIsSeller] = useState(false);

  const uid = user.id?.toString() || '';
  const displayName = user.displayName?.toString() || user.name?.toString() || 'Unknown';

  const rawUsername = user.username?.toString() || '';
  const username = rawUsername ? (rawUsername.startsWith('@') ? rawUsername : `@${rawUsername}`) : '';

  const avatarUrl = user.profileImage?.toString() || user.photoURL?.toString() || '';

  // Firestore stores this field as `isVerified`, not `verified` — same
  // fallback UserItem uses.
  const verified = user.isVerified === true || user.verified === true;
  const isVerifiedSeller = user.isVerifiedSeller === true || user.isVerifiedSeller === 'true';

  const verification = user.verification;
  const nestedType = verification && typeof verification === 'object' ? verification.categoryType : null;
  const categoryType = nestedType ?? user.categoryType;
  const isBusinessCategory = categoryType === 'Business&Organizations';

  const currentUid = auth.currentUser?.uid || '';
  const isSelf = currentUid === uid;

  useEffect(() => {
    let cancelled = false;
    async function checkIsSeller() {
      if (!uid) return;
      try {
        const q = query(collection(db, 'listings'), where('uid', '==', uid), limit(1));
        const snap = await getDocs(q);
        if (!cancelled) setIsSeller(!snap.empty);
      } catch (e) {
        if (!cancelled) setIsSeller(false);
      }
    }
    checkIsSeller();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const openProfile = () => {
    if (uid) navigate(`/profile/${uid}`);
  };

  const handleSubscribeToggle = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) return;
    await subscribe.toggleSubscribe(uid);
  };

  const showIdBadge = isVerifiedSeller;
  const showSellerBadge = isSeller;

  const isSubscribed = subscribe?.isSubscribedTo?.(uid);
  const shouldSubBack = subscribe?.shouldSubBack?.(uid);

  let buttonText;
  if (isSubscribed) buttonText = 'Subscribed';
  else if (shouldSubBack) buttonText = 'Sub Back';
  else buttonText = 'Subscribe';

  return (
    <div
      onClick={openProfile}
      style={{ width, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}
    >
      <UserAvatar size={56} imageUrl={avatarUrl || undefined} name={displayName} />
      <div style={{ height: 8, flexShrink: 0 }} />

      {/* Name + verification badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0, maxWidth: '100%' }}>
        <span
          style={{
            color: '#fff',
            fontSize: 12.5,
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          {displayName}
        </span>
        {verified && (
          <>
            <div style={{ width: 3, flexShrink: 0 }} />
            <VerificationBadge size={13} />
          </>
        )}
      </div>

      {username && (
        <div
          style={{
            marginTop: 2,
            color: 'rgba(255,255,255,0.45)',
            fontSize: 11,
            fontWeight: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            maxWidth: '100%',
          }}
        >
          {username}
        </div>
      )}

      {/* Badges (ID Verified + Listed) */}
      {(showIdBadge || showSellerBadge) && (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
          {showIdBadge && <IDVerificationBadge fontSize={8} isBusiness={isBusinessCategory} />}
          {showSellerBadge && (
            <span
              style={{
                backgroundColor: '#2ECC71',
                borderRadius: 10,
                padding: '2px 6px',
                color: '#fff',
                fontSize: 8.5,
                fontWeight: 600,
              }}
            >
              Listed
            </span>
          )}
        </div>
      )}

      <div style={{ height: 8, flexShrink: 0 }} />

      {/* Subscribe button */}
      {!isSelf && uid && (
        <button
          onClick={handleSubscribeToggle}
          style={{
            width: '100%',
            padding: '7px 0',
            borderRadius: 8,
            border: isSubscribed ? '1px solid rgba(255,255,255,0.15)' : 'none',
            backgroundColor: isSubscribed ? CARD : ORANGE,
            cursor: 'pointer',
            transition: 'background-color 200ms ease-in-out',
          }}
        >
          <span
            style={{
              display: 'block',
              color: isSubscribed ? 'rgba(255,255,255,0.70)' : '#000',
              fontSize: 11,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {buttonText}
          </span>
        </button>
      )}
    </div>
  );
}

export { UserRecommendationItem };